"use client";

import React, { useState, useEffect } from "react";
import {
  Button,
  Table,
  Tag,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  Popconfirm,
  Spin,
  message,
  Modal,
  Radio,
  Switch,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import useNotificationsStore, { Notification } from "./NotificationsStore.ts";

const { TextArea } = Input;
const { Option } = Select;

export default function Notifications() {
  const {
    getNotifications,
    getNotificationById,
    sendToUser,
    sendToUsers,
    broadcast,
    deleteNotification,
    loadUsers,
    clearUsersList,
    notificationsData,
    notificationDetail,
    isListLoading,
    isDetailLoading,
    isSaving,
    usersList,
    isLoadingUsers,
  } = useNotificationsStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [sendType, setSendType] = useState<"user" | "users" | "broadcast">("user");
  const [sendForm] = Form.useForm();

  useEffect(() => {
    fetchNotifications();
  }, [currentPage, pageSize]);

  const fetchNotifications = () => {
    const params: any = {
      page: currentPage,
      per_page: pageSize,
    };
    getNotifications(params);
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);
  };

  const handleView = async (id: number) => {
    const res = await getNotificationById(id);
    if (res.success && res.data) {
      setIsDrawerOpen(true);
    } else {
      message.error("Không thể tải chi tiết thông báo");
    }
  };

  const handleDelete = async (id: number) => {
    const res = await deleteNotification(id);
    if (res.success) {
      message.success("Đã xóa thông báo");
      fetchNotifications();
    }
  };

  const handleOpenSendModal = () => {
    setIsSendModalOpen(true);
    setSendType("user");
    sendForm.resetFields();
    clearUsersList();
    // Load users khi mở modal
    loadUsers();
  };

  const handleSearchUsers = async (search: string) => {
    if (!search || search.length < 2) {
      // Nếu không có search hoặc quá ngắn, load danh sách ban đầu
      loadUsers();
      return;
    }
    // Tìm kiếm với từ khóa
    loadUsers(search);
  };

  const handleSendNotification = async () => {
    try {
      const values = await sendForm.validateFields();
      let res;

      if (sendType === "user") {
        res = await sendToUser({
          user_id: values.user_id,
          title: values.title,
          message: values.message,
          type: values.type,
          data: values.data,
          push: values.push,
        });
      } else if (sendType === "users") {
        res = await sendToUsers({
          user_ids: values.user_ids,
          title: values.title,
          message: values.message,
          type: values.type,
          data: values.data,
          push: values.push,
        });
      } else {
        res = await broadcast({
          title: values.title,
          message: values.message,
          type: values.type,
          data: values.data,
          push: values.push,
        });
      }

      if (res.success) {
        message.success("Gửi thông báo thành công");
        setIsSendModalOpen(false);
        sendForm.resetFields();
        fetchNotifications();
      } else {
        message.error(res.message || "Gửi thông báo thất bại");
      }
    } catch (error) {
      console.error("Error sending notification:", error);
    }
  };

  const columns: ColumnsType<Notification> = [
    {
      title: (
        <span className="flex items-center gap-2">
          <i className="fa-solid fa-bell text-black"></i>
          <span className="font-semibold">Thông báo</span>
        </span>
      ),
      key: "notification",
      render: (_, record) => (
        <div>
          <div className="font-semibold text-gray-900">{record.title}</div>
          <div className="mt-1 text-xs text-gray-500 line-clamp-2">
            {record.content}
          </div>
          <div className="mt-1 text-xs text-gray-400">
            {new Date(record.created_at).toLocaleString("vi-VN", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </div>
      ),
    },
    {
      title: (
        <span className="flex items-center gap-2">
          <i className="fa-solid fa-user text-black"></i>
          <span className="font-semibold">Người nhận</span>
        </span>
      ),
      key: "recipient",
      render: (_, record) => {
        if (record.is_broadcast) {
          return <Tag color="blue">Tất cả người dùng</Tag>;
        } else if (record.user) {
          return (
            <div className="space-y-1 text-sm">
              <div className="font-medium text-gray-900">{record.user.name}</div>
              <div className="text-xs text-gray-500">{record.user.email}</div>
            </div>
          );
        } else {
          return <Tag>N/A</Tag>;
        }
      },
    },
    {
      title: (
        <span className="flex items-center gap-2">
          <i className="fa-solid fa-tag text-black"></i>
          <span className="font-semibold">Loại</span>
        </span>
      ),
      key: "type",
      render: (_, record) => {
        if (record.is_broadcast) {
          return <Tag color="purple">Broadcast</Tag>;
        } else if (record.user_id) {
          return <Tag color="green">Gửi cá nhân</Tag>;
        }
        return <Tag>N/A</Tag>;
      },
    },
    {
      title: <span className="font-semibold">Actions</span>,
      key: "action",
      align: "center",
      width: 160,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
            icon={<i className="fa-solid fa-eye" />}
            onClick={() => handleView(record.id)}
          />
          <Popconfirm
            title="Xóa thông báo này?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              className="hover:bg-red-50"
              icon={<i className="fa-solid fa-trash" />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const paginationConfig = {
    current: notificationsData?.current_page || 1,
    pageSize: notificationsData?.per_page || 10,
    total: notificationsData?.total || 0,
    showSizeChanger: true,
    showTotal: (total: number, range: [number, number]) =>
      `${range[0]}-${range[1]} of ${total} notifications`,
  };

  return (
    <>
      <div className="mb-8">
        <div className="flex items-center gap-2 text-sm text-gray-400 mb-2 font-medium">
          <span>Home</span>
          <span>/</span>
          <span className="text-black">Notifications</span>
        </div>
        <h1 className="text-3xl font-bold text-black">Notification Management</h1>
      </div>

      <div className="bg-white rounded-3xl shadow-soft border border-gray-100 overflow-hidden">
        <div className="p-6">
          <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
            <Button
              type="primary"
              icon={<i className="fa-solid fa-paper-plane mr-2" />}
              onClick={handleOpenSendModal}
              size="large"
            >
              Gửi thông báo
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={notificationsData?.data}
            loading={isListLoading}
            rowKey="id"
            pagination={paginationConfig}
            onChange={handleTableChange}
            scroll={{ x: 900 }}
          />
        </div>
      </div>

      {/* Detail Drawer */}
      <Drawer
        title={
          <span className="flex items-center gap-2 text-lg">
            <i className="fa-solid fa-bell text-black"></i>
            Chi tiết thông báo
          </span>
        }
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        width={600}
        destroyOnClose
      >
        {isDetailLoading || !notificationDetail ? (
          <div className="flex justify-center items-center h-40">
            <Spin tip="Đang tải..." />
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-bold mb-2">{notificationDetail.title}</h3>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                {notificationDetail.content}
              </p>
            </div>

            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500">Loại:</span>
                <span>
                  {notificationDetail.is_broadcast ? (
                    <Tag color="purple">Broadcast</Tag>
                  ) : (
                    <Tag color="green">Gửi cá nhân</Tag>
                  )}
                </span>
              </div>
              {notificationDetail.user && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Người nhận:</span>
                  <span className="font-medium">{notificationDetail.user.name}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Ngày tạo:</span>
                <span>
                  {new Date(notificationDetail.created_at).toLocaleString("vi-VN")}
                </span>
              </div>
            </div>
          </div>
        )}
      </Drawer>

      {/* Send Notification Modal */}
      <Modal
        title={
          <span className="flex items-center gap-2">
            <i className="fa-solid fa-paper-plane text-black"></i>
            Gửi thông báo
          </span>
        }
        open={isSendModalOpen}
        onCancel={() => {
          setIsSendModalOpen(false);
          sendForm.resetFields();
          clearUsersList();
        }}
        footer={[
          <Button key="cancel" onClick={() => setIsSendModalOpen(false)}>
            Hủy
          </Button>,
          <Button
            key="send"
            type="primary"
            onClick={handleSendNotification}
            loading={isSaving}
          >
            Gửi
          </Button>,
        ]}
        width={600}
      >
        <Form form={sendForm} layout="vertical">
          <Form.Item label="Loại gửi">
            <Radio.Group
              value={sendType}
              onChange={(e) => {
                const newType = e.target.value;
                setSendType(newType);
                sendForm.setFieldsValue({
                  user_id: undefined,
                  user_ids: undefined,
                });
                // Load users khi chuyển sang chế độ user hoặc users
                if (newType === "user" || newType === "users") {
                  if (usersList.length === 0) {
                    loadUsers();
                  }
                }
              }}
            >
              <Radio value="user">Gửi cho 1 user</Radio>
              <Radio value="users">Gửi cho nhiều users</Radio>
              <Radio value="broadcast">Broadcast (tất cả users)</Radio>
            </Radio.Group>
          </Form.Item>

          {sendType === "user" && (
            <Form.Item
              name="user_id"
              label="Chọn user"
              rules={[{ required: true, message: "Vui lòng chọn user" }]}
            >
              <Select
                showSearch
                placeholder="Tìm kiếm user..."
                filterOption={false}
                onSearch={handleSearchUsers}
                onFocus={() => {
                  // Load users khi focus vào Select nếu chưa có dữ liệu
                  if (usersList.length === 0) {
                    loadUsers();
                  }
                }}
                loading={isLoadingUsers}
                notFoundContent={isLoadingUsers ? <Spin size="small" /> : "Không tìm thấy"}
              >
                {usersList.map((user) => (
                  <Option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {sendType === "users" && (
            <Form.Item
              name="user_ids"
              label="Chọn nhiều users"
              rules={[{ required: true, message: "Vui lòng chọn ít nhất 1 user" }]}
            >
              <Select
                mode="multiple"
                showSearch
                placeholder="Tìm kiếm và chọn users..."
                filterOption={false}
                onSearch={handleSearchUsers}
                onFocus={() => {
                  // Load users khi focus vào Select nếu chưa có dữ liệu
                  if (usersList.length === 0) {
                    loadUsers();
                  }
                }}
                loading={isLoadingUsers}
                notFoundContent={isLoadingUsers ? <Spin size="small" /> : "Không tìm thấy"}
              >
                {usersList.map((user) => (
                  <Option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="title"
            label="Tiêu đề"
            rules={[
              { required: true, message: "Vui lòng nhập tiêu đề" },
              { max: 255, message: "Tiêu đề không được vượt quá 255 ký tự" },
            ]}
          >
            <Input placeholder="Nhập tiêu đề thông báo..." maxLength={255} />
          </Form.Item>

          <Form.Item
            name="message"
            label="Nội dung"
            rules={[
              { required: true, message: "Vui lòng nhập nội dung" },
              { max: 1000, message: "Nội dung không được vượt quá 1000 ký tự" },
            ]}
          >
            <TextArea
              rows={6}
              placeholder="Nhập nội dung thông báo..."
              maxLength={1000}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="type"
            label="Loại thông báo (tùy chọn)"
            rules={[{ max: 50, message: "Loại không được vượt quá 50 ký tự" }]}
          >
            <Input placeholder="Nhập loại thông báo..." maxLength={50} />
          </Form.Item>

          <Form.Item
            name="push"
            label="Gửi push notification"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

